export interface ICinemaPalette {
  pageTextColor: string;
  linkColor: string;
  buttonBackgroundColor: string;
  buttonHoverColor: string;
  accentHi: string;
  accentGlow: string;
  linkHi: string;
  ctaInk: string;
}

const DEFAULT_PALETTE = {
  pageTextColor: '#14171d',
  linkColor: '#ff2d43',
  buttonBackgroundColor: '#c4001a',
  buttonHoverColor: '#a30016',
};

const expandHex = (value: string): string | null => {
  const raw = value.replace('#', '').trim();

  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return raw
      .split('')
      .map((char) => char + char)
      .join('');
  }

  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return raw;
  }

  return null;
};

const hexToRgb = (hex: string): [number, number, number] | null => {
  const expanded = expandHex(hex);

  if (!expanded) {
    return null;
  }

  return [
    parseInt(expanded.slice(0, 2), 16),
    parseInt(expanded.slice(2, 4), 16),
    parseInt(expanded.slice(4, 6), 16),
  ];
};

const safeHex = (value: unknown): string | null => {
  if (typeof value !== 'string' || !expandHex(value)) {
    return null;
  }

  return value;
};

export const relativeLuminance = (hex: string): number => {
  const rgb = hexToRgb(hex);

  if (!rgb) {
    return 0;
  }

  const [r, g, b] = rgb
    .map((channel) => channel / 255)
    .map((channel) =>
      channel <= 0.03928
        ? channel / 12.92
        : Math.pow((channel + 0.055) / 1.055, 2.4),
    );

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const mixWhite = (hex: string, whiteShare: number): string => {
  const rgb = hexToRgb(hex);

  if (!rgb) {
    return '#ffffff';
  }

  return (
    '#' +
    rgb
      .map((channel) =>
        Math.round(channel * (1 - whiteShare) + 255 * whiteShare),
      )
      .map((channel) => channel.toString(16).padStart(2, '0'))
      .join('')
  );
};

export const normalize = (hex: string): string => {
  let whiteShare = 0.55;
  let mixed = mixWhite(hex, whiteShare);

  while (relativeLuminance(mixed) < 0.4 && whiteShare < 0.95) {
    whiteShare += 0.05;
    mixed = mixWhite(hex, whiteShare);
  }

  return mixed;
};

export const withAlpha = (hex: string, alpha: number): string => {
  const rgb = hexToRgb(hex);

  if (!rgb) {
    return `rgba(255,255,255,${alpha})`;
  }

  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
};

export const buildCinemaPalette = (appTheme: any): ICinemaPalette => {
  const buttonBackgroundColor =
    safeHex(appTheme?.submitButton?.backgroundColor) ??
    DEFAULT_PALETTE.buttonBackgroundColor;
  const buttonHoverColor =
    safeHex(appTheme?.submitButton?.backgroundColorHover) ??
    DEFAULT_PALETTE.buttonHoverColor;
  const linkColor =
    safeHex(appTheme?.loginPage?.assets?.helperTextLinkColor) ??
    DEFAULT_PALETTE.linkColor;
  const pageTextColor =
    safeHex(appTheme?.loginPage?.assets?.helperTextColor) ??
    DEFAULT_PALETTE.pageTextColor;

  return {
    pageTextColor,
    linkColor,
    buttonBackgroundColor,
    buttonHoverColor,
    accentHi: normalize(buttonBackgroundColor),
    accentGlow: withAlpha(buttonBackgroundColor, 0.55),
    linkHi: normalize(linkColor),
    ctaInk:
      relativeLuminance(buttonBackgroundColor) > 0.5
        ? pageTextColor
        : '#ffffff',
  };
};
